import { Injectable } from '@angular/core';
import { StorageService } from '../../utils/storage/storage.service';
import { CLOCK_TICK, DAY_CHANGED, SERVER_CHANGED } from '../../utils/watcher/watcher-event';
import { WatcherService } from '../../utils/watcher/watcher.service';

import find from 'lodash/find';

import * as moment from 'moment-timezone';
import { Subscription, timer } from 'rxjs';
import { UtilityClass } from '../../utils/UtilityClass';

export enum SERVER_TYPE {
  DEVEL = 'syd_devel',
  PROD_UK = 'production_uk',
  PROD_AU_NZ = 'production_au_nz',
  LOCAL = 'local',
}

export interface SLS_SERVICE {
  url: string;
  token: string;
}

export interface IServer {
  label: string;
  url: string;
  bff: SLS_SERVICE;
  env: SERVER_TYPE;
  currency: string;
  print_codepage: string;
  fallback?: SERVER_TYPE;
}

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  public DEV_MODE: boolean = false;
  private _devClicks: number = 0;

  // DEVICE INFORMATION
  public appDeviceInfo: {
    device: {
      uuID: string;
      manufacturer: string;
      model: string;
      version: string;
      platform: string;
    };
  } = {
    device: {
      uuID: '',
      manufacturer: '',
      model: '',
      version: '',
      platform: '',
    },
  };

  /**
   * The number of times the login will attempt to retry.
   * This number usually reflects how many servers the app will hit
   * on release. 2 = Production AUNZ and Production UK
   */
  public readonly fallback_check_count: number = 2;

  /**
   * The number of minutes before HungryHungry sends an SMS to the customer
   * for not recognising the order
   */
  public readonly time_to_send_ETA_sms: number = 14;

  /**
   * The number of minutes from created to requested time before an order is
   * considered ASAP.
   */
  public readonly asap_required_time: number = 2;

  /**
   * List of available servers
   */
  private _servers: IServer[] = [
    {
      label: 'Production AU&NZ',
      url: 'https://platform.hungryhungry.com/',
      bff: {
        url: 'https://om-bff-service.prod.ap-southeast-2.hungrycoder.net/',
        token: 'eyJhcHAiOiJvbS1iZmYiLCJzZXJ2ZXIiOiJQcm9kQVUiLCJpYXQiOjE1MTYyMzkwMjJ9',
      },
      env: SERVER_TYPE.PROD_AU_NZ,
      currency: 'AUD',
      print_codepage: 'default',
    },
    {
      label: 'Devel',
      url: 'https://admin.dev.ap-southeast-2.hungrycoder.net/',
      bff: {
        url: 'https://om-bff-service.dev.ap-southeast-2.hungrycoder.net/',
        token: 'eyJwcm9qZWN0IjoiYmZmIiwibmFtZSI6Im9tLWJmZi1zZXJ2aWNlIiwiaWF0IjoxNjM4MjI1NjE2fQ',
      },
      env: SERVER_TYPE.DEVEL,
      currency: 'AUD',
      print_codepage: 'default',
    },
    {
      label: 'Local',
      url: 'https://local.admin/',
      bff: {
        url: '',
        token: '',
      },
      env: SERVER_TYPE.LOCAL,
      currency: 'AUD',
      print_codepage: 'default',
    },
  ];

  /**
   * The active server selected. This can be populated by
   * fallback login.
   */
  private _active_server: IServer = this._servers[0];

  /**
   * The timezone to use for the server time
   */
  private _timezone: string;

  /**
   * The server time based on timezone. This is a moment object.
   */
  private _server_time: moment.Moment;

  /** The clock tick subscription */
  private _rxjsInterval: Subscription;

  constructor(public storage: StorageService, public watcher: WatcherService) {}

  /**
   * Starts the clock tick to be in sync with server time
   */
  private _startClock() {
    if (this._rxjsInterval) this._rxjsInterval.unsubscribe();

    if (this._server_time) {
      const remainingSec: number = Math.max(0, (60 - this._server_time.second()) * 1000);
      this._rxjsInterval = timer(remainingSec, 60000).subscribe(tick => {
        const newTime = UtilityClass.momentNow(this.Timezone);
        let dayChanged: boolean = !newTime.isSame(this._server_time, 'day');

        // now update the time
        this._server_time = newTime;
        if (dayChanged) {
          this.watcher.broadcast(DAY_CHANGED);
        }

        this.watcher.broadcast(CLOCK_TICK);
      });

      // force an initial update
      this.watcher.broadcast(CLOCK_TICK);
    }
  }

  /**
   * Sets the active server
   * @param env - The new server environment to activate
   */
  public setServerByEnv(env: SERVER_TYPE): void {
    const activeServer: IServer = find(this._servers, (server: IServer) => server.env == env);
    if (activeServer) {
      this.Active_server = activeServer;
    } else {
      this.Active_server = this._servers[0];
    }

    this.watcher.broadcast(SERVER_CHANGED, this.Active_server);
    this.storage.save('server', this.Active_server.env);
  }

  public enableDevMode() {
    this._devClicks++;

    if (this._devClicks == 12) {
      this.DEV_MODE = true;
      this._devClicks = 0;
    }
  }

  public get Active_server(): IServer {
    return this._active_server;
  }
  public set Active_server(server: IServer) {
    this._active_server = server;
  }

  public get Timezone(): string {
    return this._timezone ? this._timezone : 'Australia/Melbourne';
  }
  public set Timezone(tz: string) {
    tz = tz.replace('|', '/');
    this._timezone = tz;

    const newTime = UtilityClass.momentNow(this.Timezone);
    let dayChanged: boolean = false;
    if (this._server_time) {
      dayChanged = !newTime.isSame(this._server_time, 'day');
    }

    this._server_time = newTime;

    if (dayChanged) {
      this.watcher.broadcast(DAY_CHANGED);
    }

    // hack - force next day
    // this._server_time = this._server_time.add("1", "day");

    // always update the clock
    this._startClock();
  }

  public get Server_time(): moment.Moment {
    return this._server_time
      ? moment(this._server_time, this.Timezone) // create a clone (should be immutable)
      : UtilityClass.momentNow(this.Timezone);
  }

  public get Available_servers(): IServer[] {
    return this._servers;
  }
}
